import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRefresh, triggerRefresh } from "@/hooks/useRefresh";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil } from "lucide-react";

const EMPTY = { name: "", contact: "", terms: "", tin: "" };

export default function Suppliers() {
  const { auth } = useAuth();
  const canEdit = ["owner", "manager"].includes(auth?.membership?.role);
  const [suppliers, setSuppliers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  useRefresh(() => api.get("/suppliers").then((r) => setSuppliers(r.data)), []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/suppliers", form);
      else await api.put(`/suppliers/${editing}`, form);
      toast.success("Supplier saved");
      setEditing(null);
      triggerRefresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-4" data-testid="suppliers-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Suppliers</h1>
        {canEdit && <Button onClick={() => { setForm(EMPTY); setEditing("new"); }} data-testid="add-supplier-btn"><Plus className="h-4 w-4 mr-1" /> Add supplier</Button>}
      </div>
      <div className="border rounded-xl bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr className="border-b">
              <th className="py-2 px-3">Name</th><th className="py-2 px-3">Contact</th>
              <th className="py-2 px-3">Terms</th><th className="py-2 px-3">TIN</th>
              {canEdit && <th className="py-2 px-3"></th>}
            </tr>
          </thead>
          <tbody>
            {suppliers.map((s) => (
              <tr key={s.supplier_id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`supplier-row-${s.supplier_id}`}>
                <td className="py-2 px-3 font-medium">{s.name}</td>
                <td className="py-2 px-3 text-muted-foreground">{s.contact}</td>
                <td className="py-2 px-3">{s.terms}</td>
                <td className="py-2 px-3">{s.tin}</td>
                {canEdit && (
                  <td className="py-2 px-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => { setForm(s); setEditing(s.supplier_id); }} data-testid={`edit-supplier-${s.supplier_id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-md" data-testid="supplier-dialog">
          <DialogHeader><DialogTitle>{editing === "new" ? "Add supplier" : "Edit supplier"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2"><Label>Name</Label>
              <Input data-testid="supplier-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
            <div className="space-y-2"><Label>Contact person / number</Label>
              <Input data-testid="supplier-contact-input" value={form.contact || ""} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Terms</Label>
                <Input data-testid="supplier-terms-input" value={form.terms || ""} onChange={(e) => setForm({ ...form, terms: e.target.value })} placeholder="e.g. Net 15" /></div>
              <div className="space-y-2"><Label>TIN</Label>
                <Input data-testid="supplier-tin-input" value={form.tin || ""} onChange={(e) => setForm({ ...form, tin: e.target.value })} /></div>
            </div>
            <Button type="submit" className="w-full" data-testid="supplier-save-btn">Save supplier</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
